/**
 * Top NCR sectors / localities for v1 programmatic pages.
 *
 * URL form: `/physiotherapy-in-:city-:sectorSlug` (no nested folders).
 * sectorSlug is unique within a city; same slug across different cities
 * is allowed (e.g. "sector-1" exists in Noida AND Gurgaon).
 *
 * Picked the highest-traffic / highest-search-volume areas per city.
 * Long tail (every Noida sector 1-168) ships in Phase 3 once we
 * confirm at least one verified centre serves the locality.
 */

export interface Sector {
  citySlug: string;
  /** Slug as it appears in the URL after the city slug. e.g. "sector-18" */
  slug: string;
  /** Display name for headings. e.g. "Sector 18" */
  displayName: string;
  /** Full label including city for breadcrumbs/lists. e.g. "Sector 18, Noida" */
  fullName: string;
  lat: number;
  lng: number;
  /** 1-2 sentence flavor used in hero/intro. */
  blurb: string;
}

export const SECTORS: Sector[] = [
  // ───── NOIDA ─────
  {
    citySlug: 'noida',
    slug: 'sector-18',
    displayName: 'Sector 18',
    fullName: 'Sector 18, Noida',
    lat: 28.5707,
    lng: 77.3260,
    blurb:
      'Sector 18 is Noida\'s premier commercial hub, with malls, offices and a high density of corporate professionals - the most common physiotherapy concerns here are tech-neck, shoulder strain and lower back pain from long desk hours.',
  },
  {
    citySlug: 'noida',
    slug: 'sector-15',
    displayName: 'Sector 15',
    fullName: 'Sector 15, Noida',
    lat: 28.5856,
    lng: 77.3119,
    blurb:
      'Sector 15 is one of Noida\'s oldest residential sectors, with an established community of older residents - knee pain therapy, arthritis management and balance training are commonly sought.',
  },
  {
    citySlug: 'noida',
    slug: 'sector-50',
    displayName: 'Sector 50',
    fullName: 'Sector 50, Noida',
    lat: 28.5734,
    lng: 77.3618,
    blurb:
      'Sector 50 is a busy mid-Noida residential and commercial area with several modern wellness clinics and gyms.',
  },
  {
    citySlug: 'noida',
    slug: 'sector-62',
    displayName: 'Sector 62',
    fullName: 'Sector 62, Noida',
    lat: 28.6206,
    lng: 77.3753,
    blurb:
      'Sector 62 is Noida\'s tech corridor with major IT companies and a working-age population that frequently needs back pain therapy and ergonomic correction.',
  },
  {
    citySlug: 'noida',
    slug: 'sector-93',
    displayName: 'Sector 93',
    fullName: 'Sector 93, Noida',
    lat: 28.5180,
    lng: 77.3895,
    blurb:
      'Sector 93 is part of Noida Expressway with high-rise residential complexes and a young, fitness-conscious community.',
  },
  {
    citySlug: 'noida',
    slug: 'sector-104',
    displayName: 'Sector 104',
    fullName: 'Sector 104, Noida',
    lat: 28.5436,
    lng: 77.3640,
    blurb:
      'Sector 104 is a residential pocket close to the Noida Expressway with modern healthcare access.',
  },
  {
    citySlug: 'noida',
    slug: 'sector-128',
    displayName: 'Sector 128',
    fullName: 'Sector 128, Noida',
    lat: 28.5128,
    lng: 77.3793,
    blurb:
      'Sector 128 is a premium residential and Jaypee golf-course area along the expressway, popular with affluent professionals seeking sports physiotherapy.',
  },
  {
    citySlug: 'noida',
    slug: 'sector-137',
    displayName: 'Sector 137',
    fullName: 'Sector 137, Noida',
    lat: 28.5316,
    lng: 77.4181,
    blurb:
      'Sector 137 is a fast-growing high-rise residential cluster on the Noida Expressway.',
  },
  {
    citySlug: 'noida',
    slug: 'sector-150',
    displayName: 'Sector 150',
    fullName: 'Sector 150, Noida',
    lat: 28.4716,
    lng: 77.4496,
    blurb:
      'Sector 150 is one of NCR\'s greenest residential sectors with planned wellness infrastructure.',
  },
  {
    citySlug: 'noida',
    slug: 'greater-noida-west',
    displayName: 'Greater Noida West',
    fullName: 'Greater Noida West (Noida Extension)',
    lat: 28.6058,
    lng: 77.4434,
    blurb:
      'Greater Noida West (Noida Extension) is a rapidly-built residential township housing young families and IT professionals.',
  },

  // ───── GHAZIABAD ─────
  {
    citySlug: 'ghaziabad',
    slug: 'indirapuram',
    displayName: 'Indirapuram',
    fullName: 'Indirapuram, Ghaziabad',
    lat: 28.6448,
    lng: 77.3735,
    blurb:
      'Indirapuram is Ghaziabad\'s most popular high-rise residential locality, with modern shopping arcades and a large working-age population. Common concerns: lower back pain, postpartum recovery and tech-neck.',
  },
  {
    citySlug: 'ghaziabad',
    slug: 'vaishali',
    displayName: 'Vaishali',
    fullName: 'Vaishali, Ghaziabad',
    lat: 28.6451,
    lng: 77.3349,
    blurb:
      'Vaishali is a planned residential township well connected to Delhi via Metro, popular with families and senior citizens.',
  },
  {
    citySlug: 'ghaziabad',
    slug: 'kaushambi',
    displayName: 'Kaushambi',
    fullName: 'Kaushambi, Ghaziabad',
    lat: 28.6463,
    lng: 77.3245,
    blurb:
      'Kaushambi sits next to the Anand Vihar interchange and serves a mixed commercial-residential population, with active orthopedic and physiotherapy clinics.',
  },
  {
    citySlug: 'ghaziabad',
    slug: 'raj-nagar-extension',
    displayName: 'Raj Nagar Extension',
    fullName: 'Raj Nagar Extension, Ghaziabad',
    lat: 28.7055,
    lng: 77.4267,
    blurb:
      'Raj Nagar Extension is a new high-rise residential cluster on NH-58 attracting young families.',
  },
  {
    citySlug: 'ghaziabad',
    slug: 'crossings-republik',
    displayName: 'Crossings Republik',
    fullName: 'Crossings Republik, Ghaziabad',
    lat: 28.6342,
    lng: 77.4351,
    blurb:
      'Crossings Republik is a township at the Noida-Ghaziabad border with modern apartments and growing healthcare access.',
  },
  {
    citySlug: 'ghaziabad',
    slug: 'vasundhara',
    displayName: 'Vasundhara',
    fullName: 'Vasundhara, Ghaziabad',
    lat: 28.6643,
    lng: 77.3704,
    blurb:
      'Vasundhara is a well-planned residential sector adjacent to Vaishali and Indirapuram.',
  },

  // ───── DELHI ─────
  {
    citySlug: 'delhi',
    slug: 'south-delhi',
    displayName: 'South Delhi',
    fullName: 'South Delhi',
    lat: 28.5494,
    lng: 77.2500,
    blurb:
      'South Delhi covers premium residential colonies including Greater Kailash, Defence Colony, Hauz Khas and Vasant Kunj, with a high concentration of specialised physiotherapy and rehab clinics.',
  },
  {
    citySlug: 'delhi',
    slug: 'saket',
    displayName: 'Saket',
    fullName: 'Saket, South Delhi',
    lat: 28.5244,
    lng: 77.2066,
    blurb:
      'Saket is a major South Delhi commercial-residential hub with multiple super-specialty hospitals and orthopedic-physiotherapy clinics.',
  },
  {
    citySlug: 'delhi',
    slug: 'dwarka',
    displayName: 'Dwarka',
    fullName: 'Dwarka, South West Delhi',
    lat: 28.5921,
    lng: 77.0460,
    blurb:
      'Dwarka is one of Asia\'s largest planned sub-cities, with a wide demographic and good access to physiotherapy across most sectors.',
  },
  {
    citySlug: 'delhi',
    slug: 'rohini',
    displayName: 'Rohini',
    fullName: 'Rohini, North West Delhi',
    lat: 28.7434,
    lng: 77.0691,
    blurb:
      'Rohini is a large planned residential township in North West Delhi with established physiotherapy and rehabilitation centres.',
  },
  {
    citySlug: 'delhi',
    slug: 'east-delhi',
    displayName: 'East Delhi',
    fullName: 'East Delhi',
    lat: 28.6280,
    lng: 77.2956,
    blurb:
      'East Delhi covers Preet Vihar, Mayur Vihar, Laxmi Nagar and surrounding areas with broad-spectrum physiotherapy access.',
  },
  {
    citySlug: 'delhi',
    slug: 'janakpuri',
    displayName: 'Janakpuri',
    fullName: 'Janakpuri, West Delhi',
    lat: 28.6219,
    lng: 77.0878,
    blurb:
      'Janakpuri is one of West Delhi\'s most established residential pockets, with a senior-skewing population that frequently seeks geriatric and orthopedic physiotherapy.',
  },
  {
    citySlug: 'delhi',
    slug: 'pitampura',
    displayName: 'Pitampura',
    fullName: 'Pitampura, North West Delhi',
    lat: 28.6987,
    lng: 77.1313,
    blurb:
      'Pitampura is a busy residential-commercial area in North West Delhi with multiple wellness and rehab clinics.',
  },

  // ───── GURGAON ─────
  {
    citySlug: 'gurgaon',
    slug: 'sector-14',
    displayName: 'Sector 14',
    fullName: 'Sector 14, Gurgaon',
    lat: 28.4707,
    lng: 77.0414,
    blurb:
      'Sector 14 is Old Gurgaon\'s established residential and commercial neighbourhood with a long-running healthcare ecosystem.',
  },
  {
    citySlug: 'gurgaon',
    slug: 'dlf-phase-1',
    displayName: 'DLF Phase 1',
    fullName: 'DLF Phase 1, Gurgaon',
    lat: 28.4768,
    lng: 77.0942,
    blurb:
      'DLF Phase 1 is a premium colony on Golf Course Road home to senior corporate executives and a high demand for sports and orthopedic physiotherapy.',
  },
  {
    citySlug: 'gurgaon',
    slug: 'dlf-phase-2',
    displayName: 'DLF Phase 2',
    fullName: 'DLF Phase 2, Gurgaon',
    lat: 28.4788,
    lng: 77.1032,
    blurb:
      'DLF Phase 2 is a corporate-residential cluster bordering Cyber City, popular with MNC employees seeking ergonomic and posture correction.',
  },
  {
    citySlug: 'gurgaon',
    slug: 'dlf-phase-3',
    displayName: 'DLF Phase 3',
    fullName: 'DLF Phase 3, Gurgaon',
    lat: 28.4938,
    lng: 77.0926,
    blurb:
      'DLF Phase 3 sits next to Cyber City and Sushant Lok with a young professional population.',
  },
  {
    citySlug: 'gurgaon',
    slug: 'sushant-lok',
    displayName: 'Sushant Lok',
    fullName: 'Sushant Lok, Gurgaon',
    lat: 28.4639,
    lng: 77.0768,
    blurb:
      'Sushant Lok is a long-established residential colony with strong access to orthopedic and physiotherapy services.',
  },
  {
    citySlug: 'gurgaon',
    slug: 'golf-course-road',
    displayName: 'Golf Course Road',
    fullName: 'Golf Course Road, Gurgaon',
    lat: 28.4451,
    lng: 77.0945,
    blurb:
      'Golf Course Road is Gurgaon\'s premium high-rise corridor housing executives, expats and athletes who frequently seek elite sports physiotherapy.',
  },
  {
    citySlug: 'gurgaon',
    slug: 'sohna-road',
    displayName: 'Sohna Road',
    fullName: 'Sohna Road, Gurgaon',
    lat: 28.4101,
    lng: 77.0379,
    blurb:
      'Sohna Road is a fast-growing high-rise corridor in southern Gurgaon with new clinics and gyms opening up.',
  },
  {
    citySlug: 'gurgaon',
    slug: 'sector-56',
    displayName: 'Sector 56',
    fullName: 'Sector 56, Gurgaon',
    lat: 28.4205,
    lng: 77.0950,
    blurb:
      'Sector 56 is a residential-commercial pocket near Golf Course Extension Road.',
  },

  // ───── FARIDABAD ─────
  {
    citySlug: 'faridabad',
    slug: 'sector-15',
    displayName: 'Sector 15',
    fullName: 'Sector 15, Faridabad',
    lat: 28.4042,
    lng: 77.3122,
    blurb:
      'Sector 15 is one of Faridabad\'s most established residential sectors with a senior-skewing population.',
  },
  {
    citySlug: 'faridabad',
    slug: 'sector-21',
    displayName: 'Sector 21',
    fullName: 'Sector 21, Faridabad',
    lat: 28.4122,
    lng: 77.3239,
    blurb:
      'Sector 21 is a mixed residential-commercial area along Mathura Road with multiple healthcare options.',
  },
  {
    citySlug: 'faridabad',
    slug: 'sector-37',
    displayName: 'Sector 37',
    fullName: 'Sector 37, Faridabad',
    lat: 28.4306,
    lng: 77.3127,
    blurb:
      'Sector 37 is a popular residential pocket of Faridabad close to NIT industrial belt.',
  },
  {
    citySlug: 'faridabad',
    slug: 'neharpar',
    displayName: 'Neharpar',
    fullName: 'Neharpar (Greater Faridabad)',
    lat: 28.4380,
    lng: 77.3300,
    blurb:
      'Neharpar (Greater Faridabad) is the new high-rise residential extension along the Faridabad bypass.',
  },
  {
    citySlug: 'faridabad',
    slug: 'old-faridabad',
    displayName: 'Old Faridabad',
    fullName: 'Old Faridabad',
    lat: 28.4089,
    lng: 77.3178,
    blurb:
      'Old Faridabad is the historical core of the city with a senior-skewing population and a long-running ecosystem of orthopedic clinics.',
  },

  // ───── BENGALURU ─────
  {
    citySlug: 'bengaluru',
    slug: 'koramangala',
    displayName: 'Koramangala',
    fullName: 'Koramangala, Bengaluru',
    lat: 12.9352,
    lng: 77.6245,
    blurb:
      'Koramangala is Bengaluru\'s startup and cafe hub, packed with young professionals and founders who spend long hours at desks - tech-neck, lower back pain and wrist strain are the most common physiotherapy concerns here.',
  },
  {
    citySlug: 'bengaluru',
    slug: 'indiranagar',
    displayName: 'Indiranagar',
    fullName: 'Indiranagar, Bengaluru',
    lat: 12.9719,
    lng: 77.6412,
    blurb:
      'Indiranagar blends premium residential living with a buzzing fitness and running culture, so sports rehab, runner\'s knee and shoulder recovery are frequently sought alongside posture correction.',
  },
  {
    citySlug: 'bengaluru',
    slug: 'whitefield',
    displayName: 'Whitefield',
    fullName: 'Whitefield, Bengaluru',
    lat: 12.9698,
    lng: 77.7500,
    blurb:
      'Whitefield is Bengaluru\'s eastern IT corridor with sprawling tech parks and gated communities - desk-job back pain, ergonomic strain and post-surgical rehab are commonly treated here, with home visits popular in the apartment townships.',
  },
  {
    citySlug: 'bengaluru',
    slug: 'hsr-layout',
    displayName: 'HSR Layout',
    fullName: 'HSR Layout, Bengaluru',
    lat: 12.9116,
    lng: 77.6474,
    blurb:
      'HSR Layout is a young, fitness-forward neighbourhood full of gyms, cyclists and runners, driving demand for sports physiotherapy, injury prevention and strength-based rehab.',
  },
  {
    citySlug: 'bengaluru',
    slug: 'jayanagar',
    displayName: 'Jayanagar',
    fullName: 'Jayanagar, Bengaluru',
    lat: 12.9250,
    lng: 77.5938,
    blurb:
      'Jayanagar is one of Bengaluru\'s oldest and most established residential areas with a large senior population - knee pain therapy, arthritis management, balance training and geriatric physiotherapy are commonly sought.',
  },
  {
    citySlug: 'bengaluru',
    slug: 'electronic-city',
    displayName: 'Electronic City',
    fullName: 'Electronic City, Bengaluru',
    lat: 12.8452,
    lng: 77.6602,
    blurb:
      'Electronic City is a major IT employment hub in south Bengaluru where long commutes and desk hours make lower back pain, sciatica and posture correction the leading physiotherapy needs.',
  },
  {
    citySlug: 'bengaluru',
    slug: 'marathahalli',
    displayName: 'Marathahalli',
    fullName: 'Marathahalli, Bengaluru',
    lat: 12.9560,
    lng: 77.7010,
    blurb:
      'Marathahalli is a dense residential-commercial belt serving the Outer Ring Road tech corridor, popular with working families seeking convenient orthopedic and home physiotherapy.',
  },
  {
    citySlug: 'bengaluru',
    slug: 'jp-nagar',
    displayName: 'JP Nagar',
    fullName: 'JP Nagar, Bengaluru',
    lat: 12.9078,
    lng: 77.5851,
    blurb:
      'JP Nagar is a well-planned south Bengaluru locality with a mix of families and professionals, with steady demand for back and knee pain therapy and post-surgical rehabilitation.',
  },
  // ───── CHANDIGARH (incl. Mohali / Panchkula tricity) ─────
  {
    citySlug: 'chandigarh',
    slug: 'sector-17',
    displayName: 'Sector 17',
    fullName: 'Sector 17, Chandigarh',
    lat: 30.7415,
    lng: 76.7829,
    blurb:
      'Sector 17 is Chandigarh\'s central business district, where long retail and office shifts make neck, shoulder and lower back pain the usual reasons to see a physiotherapist.',
  },
  {
    citySlug: 'chandigarh',
    slug: 'sector-19',
    displayName: 'Sector 19',
    fullName: 'Sector 19, Chandigarh',
    lat: 30.7295,
    lng: 76.7920,
    blurb:
      'Sector 19 is a settled residential sector close to the city centre, with an older resident base and steady demand for knee pain therapy, arthritis management and balance work.',
  },
  {
    citySlug: 'chandigarh',
    slug: 'sector-34',
    displayName: 'Sector 34',
    fullName: 'Sector 34, Chandigarh',
    lat: 30.7180,
    lng: 76.7690,
    blurb:
      'Sector 34 mixes offices, coaching centres and clinics, and sees a young desk-bound crowd coming in for posture correction and tech-neck.',
  },
  {
    citySlug: 'chandigarh',
    slug: 'mohali',
    displayName: 'Mohali',
    fullName: 'Mohali, Chandigarh tricity',
    lat: 30.7046,
    lng: 76.7179,
    blurb:
      'Mohali\'s IT parks and newer housing draw younger families and professionals, with sports injuries and desk-job back pain the most common complaints.',
  },
  {
    citySlug: 'chandigarh',
    slug: 'kharar',
    displayName: 'Kharar',
    fullName: 'Kharar, Chandigarh tricity',
    lat: 30.7463,
    lng: 76.6470,
    blurb:
      'Kharar has grown quickly along the Chandigarh-Ludhiana highway, and its newer residential pockets are underserved for in-clinic and home physiotherapy.',
  },
  {
    citySlug: 'chandigarh',
    slug: 'panchkula',
    displayName: 'Panchkula',
    fullName: 'Panchkula, Chandigarh tricity',
    lat: 30.6942,
    lng: 76.8606,
    blurb:
      'Panchkula is a quieter tricity sector grid with a large retired population, where geriatric mobility, arthritis and post-surgical rehabilitation lead demand.',
  },
  {
    citySlug: 'chandigarh',
    slug: 'zirakpur',
    displayName: 'Zirakpur',
    fullName: 'Zirakpur, Chandigarh tricity',
    lat: 30.6425,
    lng: 76.8173,
    blurb:
      'Zirakpur sits at the tricity\'s southern entry point and has expanded fast into high-rise housing, with growing demand for home physiotherapy.',
  },

  // ───── JAMSHEDPUR ─────
  {
    citySlug: 'jamshedpur',
    slug: 'bistupur',
    displayName: 'Bistupur',
    fullName: 'Bistupur, Jamshedpur',
    lat: 22.8046,
    lng: 86.1836,
    blurb:
      'Bistupur is Jamshedpur\'s main commercial and medical district, and is where most residents start looking when they need a specialist physiotherapist.',
  },
  {
    citySlug: 'jamshedpur',
    slug: 'sakchi',
    displayName: 'Sakchi',
    fullName: 'Sakchi, Jamshedpur',
    lat: 22.8046,
    lng: 86.2029,
    blurb:
      'Sakchi is a dense market and residential area in central Jamshedpur, with steady demand for back pain therapy and post-injury rehabilitation.',
  },
  {
    citySlug: 'jamshedpur',
    slug: 'kadma',
    displayName: 'Kadma',
    fullName: 'Kadma, Jamshedpur',
    lat: 22.7925,
    lng: 86.1706,
    blurb:
      'Kadma is a largely residential west Jamshedpur locality with several clinics and hospitals nearby, and a resident base that leans towards knee and joint care.',
  },
  {
    citySlug: 'jamshedpur',
    slug: 'sonari',
    displayName: 'Sonari',
    fullName: 'Sonari, Jamshedpur',
    lat: 22.8017,
    lng: 86.1580,
    blurb:
      'Sonari is a calm riverside neighbourhood popular with families and retirees, where geriatric mobility and arthritis care are commonly sought.',
  },
  {
    citySlug: 'jamshedpur',
    slug: 'mango',
    displayName: 'Mango',
    fullName: 'Mango, Jamshedpur',
    lat: 22.8256,
    lng: 86.2130,
    blurb:
      'Mango is one of Jamshedpur\'s largest and fastest-growing residential belts, north of the river, and is under-supplied for physiotherapy relative to its size.',
  },
  {
    citySlug: 'jamshedpur',
    slug: 'adityapur',
    displayName: 'Adityapur',
    fullName: 'Adityapur, Jamshedpur',
    lat: 22.7868,
    lng: 86.1499,
    blurb:
      'Adityapur\'s industrial estate means a working population with a high share of repetitive-strain, shoulder and lower back complaints.',
  },
  {
    citySlug: 'jamshedpur',
    slug: 'golmuri',
    displayName: 'Golmuri',
    fullName: 'Golmuri, Jamshedpur',
    lat: 22.8046,
    lng: 86.2265,
    blurb:
      'Golmuri is an established east Jamshedpur locality around the works and hospital, with demand skewing to post-surgical and orthopedic rehabilitation.',
  },

  // ───── RANCHI ─────
  {
    citySlug: 'ranchi',
    slug: 'lalpur',
    displayName: 'Lalpur',
    fullName: 'Lalpur, Ranchi',
    lat: 23.3752,
    lng: 85.3333,
    blurb:
      'Lalpur is central Ranchi\'s commercial hub and the easiest part of the city to reach from most neighbourhoods, which is why many clinics sit here.',
  },
  {
    citySlug: 'ranchi',
    slug: 'kanke-road',
    displayName: 'Kanke Road',
    fullName: 'Kanke Road, Ranchi',
    lat: 23.3866,
    lng: 85.3196,
    blurb:
      'Kanke Road is a hospital-heavy corridor in north Ranchi, so post-surgical and neurological rehabilitation are commonly sought here.',
  },
  {
    citySlug: 'ranchi',
    slug: 'harmu',
    displayName: 'Harmu',
    fullName: 'Harmu, Ranchi',
    lat: 23.3524,
    lng: 85.2966,
    blurb:
      'Harmu is a planned residential colony with a settled, older population, and sees steady demand for knee pain therapy and mobility work.',
  },
  {
    citySlug: 'ranchi',
    slug: 'doranda',
    displayName: 'Doranda',
    fullName: 'Doranda, Ranchi',
    lat: 23.3358,
    lng: 85.3197,
    blurb:
      'Doranda is a dense south Ranchi locality mixing housing, offices and markets, with back and neck pain the usual reasons to book.',
  },
  {
    citySlug: 'ranchi',
    slug: 'bariatu',
    displayName: 'Bariatu',
    fullName: 'Bariatu, Ranchi',
    lat: 23.3925,
    lng: 85.3369,
    blurb:
      'Bariatu sits beside Ranchi\'s main medical campus, so referrals here lean towards post-operative and neurological rehabilitation.',
  },
  {
    citySlug: 'ranchi',
    slug: 'ratu-road',
    displayName: 'Ratu Road',
    fullName: 'Ratu Road, Ranchi',
    lat: 23.3742,
    lng: 85.2910,
    blurb:
      'Ratu Road is a busy west Ranchi artery with fast-growing residential pockets on either side and limited physiotherapy supply.',
  },
  // ───── MUMBAI ─────
  {
    citySlug: 'mumbai',
    slug: 'kandivali-west',
    displayName: 'Kandivali West',
    fullName: 'Kandivali West, Mumbai',
    lat: 19.2055,
    lng: 72.8354,
    blurb:
      'Kandivali West is a dense residential suburb along the Western line and New Link Road, where long train commutes and high-rise living drive steady demand for back, neck and knee physiotherapy.',
  },
  {
    citySlug: 'mumbai',
    slug: 'malad-west',
    displayName: 'Malad West',
    fullName: 'Malad West, Mumbai',
    lat: 19.1868,
    lng: 72.8399,
    blurb:
      'Malad West mixes office parks at Mindspace with large residential pockets, so desk-related neck and back strain sits alongside knee pain in older residents.',
  },
  {
    citySlug: 'mumbai',
    slug: 'borivali-west',
    displayName: 'Borivali West',
    fullName: 'Borivali West, Mumbai',
    lat: 19.2307,
    lng: 72.8567,
    blurb:
      'Borivali West is a family-heavy suburb at the northern end of the Western line, with strong demand for geriatric physiotherapy, knee care and post-surgical rehab close to home.',
  },
  {
    citySlug: 'mumbai',
    slug: 'andheri-west',
    displayName: 'Andheri West',
    fullName: 'Andheri West, Mumbai',
    lat: 19.1364,
    lng: 72.8296,
    blurb:
      'Andheri West is a media and hospitality hub around Lokhandwala, Versova and Juhu, where irregular hours and gym or dance injuries make sports physiotherapy and posture correction common needs.',
  },
  {
    citySlug: 'mumbai',
    slug: 'bandra-west',
    displayName: 'Bandra West',
    fullName: 'Bandra West, Mumbai',
    lat: 19.0596,
    lng: 72.8295,
    blurb:
      'Bandra West is an affluent, fitness-focused suburb where running, cycling and gym injuries are frequent, and where patients expect one-on-one physiotherapy with clear recovery plans.',
  },
  {
    citySlug: 'mumbai',
    slug: 'south-mumbai',
    displayName: 'South Mumbai',
    fullName: 'South Mumbai, Mumbai',
    lat: 18.9388,
    lng: 72.8354,
    blurb:
      'South Mumbai spans Colaba to Gamdevi and Malabar Hill, with an older resident population and heritage buildings without lifts, so home physiotherapy, knee care and mobility work are in high demand.',
  },
  // ───── AHMEDABAD ─────
  {
    citySlug: 'ahmedabad',
    slug: 'thaltej',
    displayName: 'Thaltej',
    fullName: 'Thaltej, Ahmedabad',
    lat: 23.0525,
    lng: 72.5085,
    blurb:
      'Thaltej sits on SG Highway near Zydus Hospital and a cluster of corporate offices, with high demand for spine care, post-surgical rehab and sports physiotherapy.',
  },
  {
    citySlug: 'ahmedabad',
    slug: 'satellite',
    displayName: 'Satellite',
    fullName: 'Satellite, Ahmedabad',
    lat: 23.0300,
    lng: 72.5150,
    blurb:
      'Satellite is one of west Ahmedabad\'s most established residential areas, where knee pain in older adults and desk-related neck and back strain are the typical reasons for a physiotherapy visit.',
  },
  {
    citySlug: 'ahmedabad',
    slug: 'bopal',
    displayName: 'Bopal',
    fullName: 'Bopal, Ahmedabad',
    lat: 23.0330,
    lng: 72.4680,
    blurb:
      'Bopal and South Bopal are fast-growing family suburbs on the western edge of the city, with rising demand for pediatric, postnatal and sports physiotherapy close to home.',
  },
  {
    citySlug: 'ahmedabad',
    slug: 'navrangpura',
    displayName: 'Navrangpura',
    fullName: 'Navrangpura, Ahmedabad',
    lat: 23.0370,
    lng: 72.5600,
    blurb:
      'Navrangpura is a central commercial and education hub around CG Road and Gujarat University, drawing students and office workers with posture, neck and lower back complaints.',
  },
  {
    citySlug: 'ahmedabad',
    slug: 'maninagar',
    displayName: 'Maninagar',
    fullName: 'Maninagar, Ahmedabad',
    lat: 22.9960,
    lng: 72.6030,
    blurb:
      'Maninagar is the largest residential and market hub in east Ahmedabad, where families look for affordable orthopedic and geriatric physiotherapy without crossing the river.',
  },
  {
    citySlug: 'ahmedabad',
    slug: 'sarkhej',
    displayName: 'Sarkhej',
    fullName: 'Sarkhej, Ahmedabad',
    lat: 22.9860,
    lng: 72.5030,
    blurb:
      'Sarkhej lies at the south-western edge of the city along the Sarkhej-Gandhinagar Highway, serving Juhapura and Makarba with clinics focused on joint pain, back pain and post-fracture rehab.',
  },
  {
    citySlug: 'ahmedabad',
    slug: 'odhav',
    displayName: 'Odhav',
    fullName: 'Odhav, Ahmedabad',
    lat: 23.0300,
    lng: 72.6660,
    blurb:
      'Odhav is an industrial belt on the eastern ring road, where factory work leads to back, shoulder and knee strain and where home-visit physiotherapy is often the practical option.',
  },
  // ───── LUCKNOW ─────
  {
    citySlug: 'lucknow',
    slug: 'gomti-nagar',
    displayName: 'Gomti Nagar',
    fullName: 'Gomti Nagar, Lucknow',
    lat: 26.8500,
    lng: 81.0000,
    blurb:
      'Gomti Nagar is Lucknow\'s planned modern district of offices, malls and apartment blocks, and its clinics see a mix of desk-related spine pain and post-surgical rehabilitation.',
  },
  {
    citySlug: 'lucknow',
    slug: 'hazratganj',
    displayName: 'Hazratganj',
    fullName: 'Hazratganj, Lucknow',
    lat: 26.8500,
    lng: 80.9450,
    blurb:
      'Hazratganj is the city\'s historic commercial centre, within easy reach of Civil Hospital and KGMU, and patients here often seek follow-up physiotherapy after hospital treatment.',
  },
  {
    citySlug: 'lucknow',
    slug: 'indira-nagar',
    displayName: 'Indira Nagar',
    fullName: 'Indira Nagar, Lucknow',
    lat: 26.8830,
    lng: 81.0100,
    blurb:
      'Indira Nagar is one of Asia\'s largest planned colonies, a family-heavy area where knee pain, back pain and geriatric mobility are the most common physiotherapy needs.',
  },
  {
    citySlug: 'lucknow',
    slug: 'aliganj',
    displayName: 'Aliganj',
    fullName: 'Aliganj, Lucknow',
    lat: 26.8900,
    lng: 80.9400,
    blurb:
      'Aliganj is a settled north Lucknow neighbourhood of government housing and older residents, with steady demand for arthritis care, post-fracture rehab and home physiotherapy.',
  },
  {
    citySlug: 'lucknow',
    slug: 'alambagh',
    displayName: 'Alambagh',
    fullName: 'Alambagh, Lucknow',
    lat: 26.8100,
    lng: 80.9000,
    blurb:
      'Alambagh is a busy transport and market hub in south Lucknow near the bus terminal and Charbagh station, serving a large working population with back, neck and knee complaints.',
  },
];

export function sectorsByCity(citySlug: string): Sector[] {
  return SECTORS.filter((s) => s.citySlug === citySlug);
}

export function findSector(citySlug: string, sectorSlug: string): Sector | undefined {
  return SECTORS.find((s) => s.citySlug === citySlug && s.slug === sectorSlug);
}
